import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, NgForm } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ApiService } from '@core/api/angular';
import { checkAdvancedSearchHistoryExists } from '@core/api/angular/operations/check-advanced-search-history-exists';
import { loading } from '@lassi/util';
import { map } from 'rxjs';

export interface SaveSearchDialogData {
  defaultName: string;
}

@Component({
  selector: 'lib-save-search-dialog',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatIcon,
  ],
  template: `
    <div class="header">
      <h1 mat-dialog-title class="mat-title-small title">Save Search</h1>
      <button mat-icon-button type="button" mat-dialog-close>
        <mat-icon>close</mat-icon>
      </button>
    </div>
    <mat-dialog-content class="dialog-content">
      <form #form="ngForm" (ngSubmit)="handleSubmit(form)">
        <mat-form-field class="full-width">
          <mat-label class="mat-label">Name</mat-label>
          <input
            #nameCtrl="ngModel"
            matInput
            required
            name="name"
            [(ngModel)]="name"
            (ngModelChange)="onNameChange()"
            maxlength="150"
            cdkFocusInitial
          />
          @if (nameCtrl.hasError('required')) {
            <mat-error class="mat-label-small">Name is required</mat-error>
          } @else if (nameCtrl.hasError('maxlength')) {
            <mat-error class="mat-label-small">
              Name cannot exceed 150 characters
            </mat-error>
          }
        </mat-form-field>

        @if (nameExistsPromptVisible()) {
          <div class="name-exists-warning mat-label-medium">
            This name already exists. You can still continue and save it.
          </div>
        }

        <button #submit type="submit" [disabled]="!name()" hidden>
          submit
        </button>
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="start" class="dialog-actions">
      <button mat-button mat-dialog-close>Cancel</button>
      @if (nameExistsPromptVisible()) {
        <button mat-flat-button (click)="saveAnyway()" [disabled]="!name()">
          Save Anyway
        </button>
      } @else {
        <button
          mat-flat-button
          (click)="submit.click()"
          [disabled]="!name() || checkHistoryLoading()"
        >
          Save
        </button>
      }
    </mat-dialog-actions>
  `,
  styles: `
    @use '@angular/material' as mat;
    @use 'theme' as theme;
    @use 'breakpoints' as *;

    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;

      > button {
        margin-right: 1rem;
      }
    }

    .dialog-content {
      padding-top: 0.5rem;
    }

    .name-exists-warning {
      margin-top: 0.5rem;
      color: var(--mat-form-field-error-text-color);
    }
    .title {
      font-weight: bold;
      margin: 0 0 1rem;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SaveSearchDialogComponent {
  private readonly apiService = inject(ApiService);
  private readonly data = inject(MAT_DIALOG_DATA) as SaveSearchDialogData;
  private readonly dialogRef = inject(MatDialogRef<SaveSearchDialogComponent>);
  private readonly destroyRef = inject(DestroyRef);

  name = signal(this.data.defaultName);
  checkHistoryLoading = signal(false);
  nameExistsPromptVisible = signal(false);

  onCancel() {
    this.dialogRef.close();
  }

  onNameChange() {
    this.nameExistsPromptVisible.set(false);
  }

  saveAnyway() {
    const name = this.name().trim();
    if (!name) return;
    this.dialogRef.close(name);
  }

  handleSubmit(form: NgForm) {
    const name = (form.value.name as string | undefined)?.trim();
    if (!name) return;

    this.name.set(name);

    this.apiService
      .invoke(checkAdvancedSearchHistoryExists, { search_name: name })
      .pipe(
        map((res) => Boolean(res.item)),
        loading(this.checkHistoryLoading),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((nameExists) => {
        if (nameExists) {
          this.nameExistsPromptVisible.set(true);
          return;
        }

        this.dialogRef.close(name);
      });
  }
}
